import { useEffect, useState } from "react";
import { X, Loader2, ArrowUpCircle, AlertCircle, CheckCircle2 } from "lucide-react";
import { fmtUsd } from "@/lib/wallet";

const API = process.env.REACT_APP_BACKEND_URL;
const NETWORKS = ["TRC20", "BEP20", "POLYGON", "ERC20"];
const MIN_CENTS = 500;   // $5.00
const NETWORK_HINTS = {
  TRC20: "Menor taxa. Recomendado para saques pequenos.",
  BEP20: "BSC · taxa muito baixa. Boa alternativa.",
  POLYGON: "Rápido e barato. Certifique-se que sua wallet suporta USDT-POL.",
  ERC20: "Ethereum · taxa alta. Use só se realmente precisar.",
};

export default function WithdrawModal({ open, balanceCents, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [addr, setAddr] = useState(() => localStorage.getItem("ufo_withdraw_addr") || "");
  const [network, setNetwork] = useState(() => localStorage.getItem("ufo_withdraw_net") || "TRC20");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const [feeCents, setFeeCents] = useState(200);  // fallback $2, refreshed from API

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/admin/public-config`)
      .then((r) => r.json())
      .then((d) => { if (typeof d?.withdraw_fee_trc20_cents === "number") setFeeCents(d.withdraw_fee_trc20_cents); })
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const amountCents = Math.round(parseFloat(amount || "0") * 100);
  const netCents = Math.max(0, amountCents - feeCents);
  const valid = (
    amountCents >= MIN_CENTS &&
    amountCents <= (balanceCents ?? 0) &&
    netCents > 0 &&
    addr.trim().length >= 10 &&
    NETWORKS.includes(network)
  );

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/wallet/withdraw`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_cents: amountCents,
          usdt_wallet_address: addr.trim(),
          usdt_network: network,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao criar saque");
      localStorage.setItem("ufo_withdraw_addr", addr.trim());
      localStorage.setItem("ufo_withdraw_net", network);
      setDone(d);
      onCreated?.(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="withdraw-modal">
      <div className="w-full max-w-md rounded-sm border border-emerald-500/40 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-300">// CASH-OUT USDT</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">SACAR SALDO</h2>
          </div>
          <button onClick={onClose} data-testid="withdraw-close"
                  className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="space-y-3">
            <div className="rounded-sm border border-emerald-400/40 bg-emerald-500/10 p-4 flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-300 shrink-0 mt-0.5" />
              <div>
                <p className="font-mono text-[10px] tracking-widest text-emerald-300">SAQUE SOLICITADO</p>
                <p className="font-display font-black text-2xl text-emerald-300 tracking-tighter">{fmtUsd(done.amount_cents)}</p>
                {done.fee_cents > 0 && (
                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                    Debitado {fmtUsd(done.gross_cents)} · Taxa {fmtUsd(done.fee_cents)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground font-mono mt-1">
                  Enviaremos o USDT para sua carteira em até 24h. Você acompanha o status na aba SACAR.
                </p>
              </div>
            </div>
            <button onClick={onClose} data-testid="withdraw-done-close"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3">
              FECHAR
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-sm border border-primary/20 bg-black/40 p-3">
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground">SALDO DISPONÍVEL</p>
              <p className="font-display font-black text-3xl tracking-tighter text-primary">{fmtUsd(balanceCents ?? 0)}</p>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">VALOR A SACAR (USDT)</p>
              <div className="flex gap-2">
                <input
                  type="number" min="5" step="0.01" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="withdraw-amount"
                  className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-base text-primary placeholder-muted-foreground/60"
                />
                <button
                  type="button"
                  onClick={() => setAmount(((balanceCents ?? 0) / 100).toFixed(2))}
                  data-testid="withdraw-max"
                  className="rounded-sm border border-primary/30 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest px-3 whitespace-nowrap"
                >
                  MÁX
                </button>
              </div>
              <p className="mt-1 font-mono text-[9px] text-muted-foreground tracking-widest">MÍNIMO {fmtUsd(MIN_CENTS)}</p>
            </div>

            {/* Fee breakdown — visible only when the user typed a valid amount */}
            {amountCents > 0 && (
              <div className="rounded-sm border border-emerald-400/30 bg-black/40 p-3 font-mono text-xs space-y-1" data-testid="withdraw-fee-breakdown">
                <div className="flex justify-between text-muted-foreground">
                  <span>Valor solicitado</span>
                  <span className="text-white">{fmtUsd(amountCents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Taxa de rede ({network})</span>
                  <span className="text-orange-300">-{fmtUsd(feeCents)}</span>
                </div>
                <div className="flex justify-between border-t border-primary/20 pt-1 mt-1">
                  <span className="text-emerald-300 font-bold">Você recebe</span>
                  <span className="text-emerald-300 font-bold text-sm" data-testid="withdraw-net-amount">{fmtUsd(netCents)}</span>
                </div>
              </div>
            )}

            <div>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">ENDEREÇO USDT</p>
              <input type="text" value={addr} onChange={(e) => setAddr(e.target.value)}
                placeholder="Cole o endereço da sua carteira de destino"
                data-testid="withdraw-addr"
                className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-xs text-primary placeholder-muted-foreground/60" />
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">REDE</p>
              <div className="grid grid-cols-4 gap-2">
                {NETWORKS.map((n) => (
                  <button key={n} onClick={() => setNetwork(n)}
                    data-testid={`withdraw-net-${n}`}
                    className={`rounded-sm border px-2 py-1 font-mono text-[10px] tracking-widest ${
                      network === n
                        ? "border-primary bg-primary/20 text-primary"
                        : "border-primary/30 text-muted-foreground"
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-2 font-mono text-[9px] text-muted-foreground/80 leading-relaxed">
                {NETWORK_HINTS[network]}
              </p>
            </div>

            <div className="flex items-start gap-2 text-xs font-mono text-amber-200 border border-amber-500/40 bg-amber-500/5 p-3 rounded-sm">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>Confira endereço e rede. Saques com endereço ou rede errada não podem ser revertidos.</p>
            </div>

            {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-300 font-mono">{err}</div>}

            <button onClick={submit} disabled={busy || !valid}
              data-testid="withdraw-confirm"
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono tracking-widest text-xs px-5 py-3 disabled:opacity-40 disabled:bg-emerald-500/30">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ArrowUpCircle className="h-3.5 w-3.5" /> SACAR {netCents > 0 ? fmtUsd(netCents) : ""}</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
